import { ReactNode, useEffect, useRef } from 'react'
import { useIsomorphicLayoutEffect } from 'usehooks-ts'

import { cx } from '@/utils/strings'
import { isPrint } from '@/utils/print'

type CollapsibleProps = {
  className?: string
  children: ReactNode
  isOpen?: boolean
  onAnimationComplete?: (isOpen: boolean) => void
  disableAnimation?: boolean
}

export default function Collapsible({
  className,
  children,
  isOpen = false,
  onAnimationComplete,
  disableAnimation = false,
}: CollapsibleProps) {
  if (isPrint()) {
    return <div className={className}>{children}</div>
  }

  const isSSR = typeof window === 'undefined'

  const containerRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const supportsInterpolateSize = !isSSR && CSS.supports?.('interpolate-size', 'allow-keywords')

  // Initialize static styles without animating on mount
  useIsomorphicLayoutEffect(() => {
    const el = containerRef.current
    const content = contentRef.current
    if (!el || !content) return

    const transition = disableAnimation
      ? 'none'
      : 'height 0.3s cubic-bezier(0.25,1,0.5,1), opacity 0.2s cubic-bezier(0.25,1,0.5,1)'

    if (isOpen) {
      Object.assign(el.style, {
        overflow: 'hidden',
        transition,
        visibility: 'visible',
        opacity: '1',
        height: supportsInterpolateSize ? 'auto' : `${content.offsetHeight}px`,
      })
    } else {
      Object.assign(el.style, {
        overflow: 'hidden',
        transition,
        visibility: 'hidden',
        opacity: '0',
        height: '0px',
      })
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    const content = contentRef.current
    if (!el || !content) return

    const finish = () => {
      if (isOpen) {
        Object.assign(el.style, {
          height: 'auto',
          visibility: 'visible',
          opacity: '1',
        })
      } else {
        Object.assign(el.style, {
          visibility: 'hidden',
          opacity: '0',
          height: '0px',
        })
      }
      onAnimationComplete?.(isOpen)
    }

    if (disableAnimation) {
      finish()
      return
    }

    const onTransitionEnd = (ev: TransitionEvent) => {
      if (ev.target !== el || ev.propertyName !== 'height') return
      el.removeEventListener('transitionend', onTransitionEnd)
      finish()
    }
    el.addEventListener('transitionend', onTransitionEnd)

    if (isOpen) {
      Object.assign(el.style, {
        height: el.offsetHeight ? `${el.offsetHeight}px` : '0px',
        visibility: 'visible',
      })

      // Force reflow to ensure transition occurs
      el.getBoundingClientRect()

      Object.assign(el.style, {
        transition: 'height 0.3s cubic-bezier(0.25,1,0.5,1), opacity 0.2s cubic-bezier(0.25,1,0.5,1)',
        height: supportsInterpolateSize ? 'auto' : `${content.offsetHeight}px`,
        opacity: '1',
      })
    } else {
      Object.assign(el.style, {
        height: supportsInterpolateSize ? 'auto' : `${content.offsetHeight}px`,
      })

      // Force reflow
      el.getBoundingClientRect()

      Object.assign(el.style, {
        transition: 'height 0.2s cubic-bezier(0.25,1,0.5,1), opacity 0.1s cubic-bezier(0.25,1,0.5,1)',
        height: '0px',
        opacity: '0',
      })
    }

    // Safety timeout in case transitionend doesn't fire
    const timeout = window.setTimeout(
      () => {
        el.removeEventListener('transitionend', onTransitionEnd)
        finish()
      },
      isOpen ? 350 : 250
    )

    return () => {
      clearTimeout(timeout)
      el.removeEventListener('transitionend', onTransitionEnd)
    }
  }, [isOpen, disableAnimation, onAnimationComplete, supportsInterpolateSize])

  return (
    <div
      ref={containerRef}
      className={cx(className, 'overflow-hidden')}
      aria-hidden={!isOpen}
      style={isSSR ? { height: '0px', visibility: 'hidden', opacity: 0 } : undefined}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  )
}
